import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, ScrollText, LogOut, ShieldCheck, Server, FileBarChart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LoginRemindersDialog } from "@/components/Reminders";

const ROLE_LABEL = { admin: "Administrator", partner: "Partner · Approver", staff: "Staff · Preparer", reader: "Reviewer · View only" };
const ROLE_COLOR = {
  admin: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  partner: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  staff: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  reader: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/clients", label: "Clients", icon: Building2 },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-60 shrink-0 border-r border-border bg-sidebar hidden md:flex flex-col">
        <div className="px-5 py-5 border-b border-border">
          <Logo size={34} subtitle="Ameya Management Group" />
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors",
                  isActive(n.to)
                    ? "bg-indigo-600 text-white border border-indigo-600 font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60 border border-transparent"
                )}
              >
                <Icon className="h-4 w-4" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border text-xs text-muted-foreground space-y-1">
          <div className="flex items-center gap-2" data-testid="portal-mode-indicator"><Server className="h-3.5 w-3.5 text-amber-400" /> GST Portal: Test mode</div>
          <div>© Ameya Management Group</div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-md bg-card/95 border-b border-border px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm">
            <Logo size={30} withText={false} className="md:hidden" />
            <span className="hidden md:flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-indigo-400" /></span>
            <span className="hidden sm:inline text-sm font-medium text-muted-foreground">GST Compliance Portal</span>
            <span className="font-head font-bold text-base md:hidden">GST<span className="text-indigo-400">Desk</span></span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <span data-testid="role-badge" className={cn("hidden sm:inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium", ROLE_COLOR[user?.role])}>
              {ROLE_LABEL[user?.role]}
            </span>
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium leading-tight">{user?.name}</div>
              <div className="text-xs text-muted-foreground font-mono">{user?.email}</div>
            </div>
            <button
              data-testid="logout-button"
              onClick={async () => { await logout(); nav("/login"); }}
              className="p-2 rounded-md border border-border text-muted-foreground hover:text-rose-400 hover:border-rose-500/40 transition-colors"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          </div>
          <nav className="md:hidden flex items-center gap-1 mt-3 overflow-x-auto">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(" ", "-")}`}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap border transition-colors",
                    isActive(n.to)
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "text-muted-foreground border-border"
                  )}
                >
                  <Icon className="h-3.5 w-3.5" /> {n.label}
                </Link>
              );
            })}
          </nav>
        </header>
        <main className="flex-1 grid-noise p-4 sm:p-6 lg:p-8">{children}<LoginRemindersDialog /></main>
      </div>
    </div>
  );
}
